import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useRedirect } from 'react-admin';
import { DocumentType } from '../../../../interfaces/document';
import referralService from '../../../../services/referral';
import { formatDateString } from '../../../../util/dateFormat';
import { Column, CustomTable } from '../../../common/CustomTable';
import { Row } from '../../../common/CustomTable/types';
import { parseDocumentRules } from '../../shared';

const DocumentTypesSetting: React.FC = () => {
  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([]);
  const [loading, setLoading] = useState(false);
  const redirect = useRedirect();

  useEffect(() => {
    const fetchDocumentTypes = async () => {
      setLoading(true);

      try {
        const data = await referralService.fetchDocumentTypes();
        if (data?.success) {
          setDocumentTypes(data.documentContents);
        }
      } catch (error) {
        console.error('Error fetching document types:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDocumentTypes();
  }, []);

  const handleEdit = (documentType: DocumentType) => {
    redirect(`/documents/settings/document-types/${documentType.id}`);
  };

  const columns: Column[] = [
    { key: 'id', label: 'ID', minWidth: 30 },
    { key: 'name', label: 'Name', minWidth: 100 },
    { key: 'parsing', label: 'Parsing Enabled', minWidth: 120 },
    { key: 'rules', label: 'Document Rules', minWidth: 200 },
    { key: 'created', label: 'Created', minWidth: 140 },
    { key: 'updated', label: 'Updated', minWidth: 140 },
    { key: 'actions', label: 'Actions', minWidth: 80, align: 'center' },
  ];

  const rows: Row[] = documentTypes.map((docType) => ({
    id: (
      <Typography variant='body2' fontWeight='medium'>
        {docType.id}
      </Typography>
    ),
    name: (
      <Box>
        <Typography variant='body2' fontWeight='medium'>
          {docType.name}
        </Typography>
      </Box>
    ),
    parsing: (
      <Chip
        label={docType.isParsingEnabled ? 'Enabled' : 'Disabled'}
        color={docType.isParsingEnabled ? 'success' : 'default'}
        size='small'
        variant='filled'
      />
    ),
    rules: (
      <Box>
        {docType.documentRules ? (
          <Box>
            <Typography variant='body2' color='text.primary'>
              Rules defined
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              {(() => {
                const parsed = parseDocumentRules(docType.documentRules);
                if (!parsed) {
                  return 'Invalid JSON format';
                }
                const required = parsed.fieldsRequired?.length || 0;
                const optional = parsed.fieldsOptional?.length || 0;
                return `${required} required, ${optional} optional`;
              })()}
            </Typography>
          </Box>
        ) : (
          <Typography variant='body2' color='text.secondary'>
            No rules defined
          </Typography>
        )}
      </Box>
    ),
    created: (
      <Typography variant='body2'>
        {formatDateString(docType.createdAt, 'MMM dd, yyyy HH:mm:ss')}
      </Typography>
    ),
    updated: (
      <Typography variant='body2'>
        {formatDateString(docType.updatedAt, 'MMM dd, yyyy HH:mm:ss')}
      </Typography>
    ),
    actions: (
      <Tooltip title='View Document Type'>
        <IconButton size='small' onClick={() => handleEdit(docType)}>
          <VisibilityIcon fontSize='small' />
        </IconButton>
      </Tooltip>
    ),
  }));

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant='h6' gutterBottom>
          Document Types
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          Manage document type configurations, parsing rules, and validation settings.
        </Typography>
      </Box>

      <CustomTable
        columns={columns}
        rows={rows}
        loading={loading}
        tableSize='small'
        defaultText='No document types found'
      />
    </Box>
  );
};

export default DocumentTypesSetting;
